import { css } from '@emotion/react';
import { useRouter } from 'next/router';

import {
  Colors,
  Field,
  Link,
  TextInput,
  Typography,
} from '@zapier/design-system';
import { addParamsToUrl } from '@zapier/url-utils';
import { urls, useSize } from '@zapier/identity-utils';
import { Button } from '@zapier/marketing-ui';
import { useTranslations } from 'next-intl';

import { useForgotPassword } from './useForgotPassword';
import {
  AuthLayout,
  CompaniesIntroPromo,
} from '@zapier/auth-pages/src/components/global';

const errorStyle = css`
  ${Typography.base}
  color: ${Colors.StatusErrorWeaker};
`;

const messageStyle = css`
  ${Typography.Body1}
  color: ${Colors.GrayWarm9};
  text-align: center;
`;

const rootStyle = css`
  display: grid;
  grid-gap: 25px;
`;

const successStyle = css`
  ${Typography.base}
  color: ${Colors.StatusSuccessWeaker};
`;

export function ForgotPasswordPage() {
  const router = useRouter();
  const size = useSize();
  const t = useTranslations('forgot-password');
  const { canSubmit, email, error, handleChangeEmail, handleSubmit, status } =
    useForgotPassword();

  function HelpLink(chunks: React.ReactNode) {
    return (
      <Link
        href={addParamsToUrl(urls.HELP_URL, {
          account: 'settings',
          from_url: router.asPath,
          settings: 'reset',
          topic: 'account',
        })}
      >
        {chunks}
      </Link>
    );
  }

  return (
    <AuthLayout
      promo={<CompaniesIntroPromo />}
      showPromo={size !== 'small'}
      title={t('title')}
      titlePosition="right"
    >
      <form css={rootStyle} onSubmit={handleSubmit}>
        <p css={messageStyle}>{t('description')}</p>
        <p css={messageStyle}>{t.rich('forgot-email', { link: HelpLink })}</p>

        <Field
          label={t('email-label')}
          inputId="reset_password_email"
          isDisabled={status === 'submitting'}
          isRequired={true}
          renderInput={inputProps => (
            <TextInput
              {...inputProps}
              autoComplete="email"
              inputMode="email"
              onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                handleChangeEmail(e.target.value)
              }
              placeholder={t('email-placeholder')}
              type="email"
              value={email}
            />
          )}
        />
        <Button
          type="submit"
          variant="brand"
          fullWidth={true}
          disabled={!canSubmit || status !== 'initial'}
        >
          {t('submit-button')}
        </Button>
        {error && <div css={errorStyle}>{error}</div>}
        {status === 'success' && <div css={successStyle}>{t('success')}</div>}
      </form>
    </AuthLayout>
  );
}
